import {
  AlertTriangle,
  ArrowRight,
  Check,
  ClipboardList,
  Clock3,
  Copy,
  Eye,
  EyeOff,
  Lightbulb,
  ListChecks,
  LoaderCircle,
  LockKeyhole,
  MessageCircleMore,
  Pause,
  Play,
  Radio,
  RotateCcw,
  Share2,
  Signal,
  SignalLow,
  Smartphone,
  Sparkles,
  Star,
  UsersRound
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import type { GameCommand, MemberView } from "../../domain/types";
import { RATING_TAGS, type RatingTagId } from "../../domain/ratings";
import { api } from "../api";
import { ErrorState, InlineNotice, LoadingState } from "../components/Feedback";
import { useRoom } from "../hooks/useRoom";

type Action = (type: GameCommand["type"], payload?: Record<string, unknown>) => Promise<unknown>;

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function SceneMedia({ view }: { view: MemberView }) {
  const [imageFailed, setImageFailed] = useState(false);
  const media = view.node.media;
  if (!media) return null;
  if (imageFailed) {
    return <p className="scene-media-fallback" role="status">La ilustración de esta escena no pudo cargarse. Puedes continuar con el texto.</p>;
  }
  return (
    <figure className="scene-media">
      <img src={media.src} alt={media.alt} width={media.width} height={media.height} decoding="async" onError={() => setImageFailed(true)} />
      {media.caption && <figcaption>{media.caption}</figcaption>}
    </figure>
  );
}

function ConnectionBadge({ state }: { state: "connecting" | "connected" | "reconnecting" | "offline" | "polling" }) {
  const labels = {
    connecting: "Conectando",
    connected: "En sincronía",
    reconnecting: "Reconectando",
    offline: "Sin red",
    polling: "Sincronizando"
  };
  return (
    <span className={`connection-badge connection-${state}`} role="status">
      {state === "connected" || state === "polling" ? <Signal aria-hidden="true" /> : <SignalLow aria-hidden="true" />}
      {labels[state]}
    </span>
  );
}

function CaseFile({ view, pending, action, open, onClose }: { view: MemberView; pending: boolean; action: Action; open: boolean; onClose: () => void }) {
  const file = view.publicState.caseFile;
  if (!file) return null;
  return (
    <aside className={open ? "case-file is-open" : "case-file"} aria-label="Expediente del caso">
      <div className="case-file-header">
        <div><span>Expediente vivo</span><h2>El caso</h2></div>
        <button className="icon-button case-file-close" type="button" aria-label="Cerrar expediente" onClick={onClose}>×</button>
      </div>
      <p className="case-objective">{file.objective}</p>
      <ol className="case-acts" aria-label="Progreso del caso">
        {file.acts.map((act) => <li key={act.id} className={act.id === file.currentActId ? "is-current" : ""}>{act.label}</li>)}
      </ol>
      <section className="case-section">
        <h3><ClipboardList aria-hidden="true" /> Pruebas ({file.evidence.length})</h3>
        {file.evidence.length ? <ul className="evidence-list">{file.evidence.map((evidence) => (
          <li key={evidence.id}>
            <div><span className="evidence-category">{evidence.category}</span><strong>{evidence.title}</strong><p>{evidence.summary}</p><small>{evidence.source}{evidence.shared ? " · compartida" : " · privada"}</small></div>
            {evidence.canShare && <button className="button button-light evidence-share" type="button" disabled={pending} onClick={() => action("share_evidence", { evidenceId: evidence.id })}>Compartir</button>}
          </li>
        ))}</ul> : <p className="case-empty">Las pruebas que encuentren aparecerán aquí y no se perderán al avanzar.</p>}
      </section>
      <section className="case-section case-suspects">
        <h3><UsersRound aria-hidden="true" /> Sospechosos</h3>
        <ul>{file.suspects.map((suspect) => <li key={suspect.id}><strong>{suspect.name}</strong><span>{suspect.role}</span><p>{suspect.summary}</p></li>)}</ul>
      </section>
      <section className="case-section">
        <h3><ListChecks aria-hidden="true" /> Cronología</h3>
        {file.timeline.length ? <ol className="case-timeline">{file.timeline.map((item) => <li key={item.id}><time>{item.time}</time><span>{item.label}</span></li>)}</ol> : <p className="case-empty">La cronología se reconstruye con las pruebas que ya tienen.</p>}
      </section>
      <section className="case-section">
        <h3>Preguntas abiertas</h3>
        <ul className="case-questions">{file.questions.map((question) => <li key={question.id} className={question.resolved ? "is-resolved" : ""}>{question.resolved ? <Check aria-hidden="true" /> : <span aria-hidden="true">?</span>}{question.text}</li>)}</ul>
      </section>
    </aside>
  );
}

function TheoryNode({ view, pending, action }: { view: MemberView; pending: boolean; action: Action }) {
  const theory = view.node.theory;
  const evidence = view.publicState.caseFile?.evidence ?? [];
  const [culpritId, setCulpritId] = useState("");
  const [motiveId, setMotiveId] = useState("");
  const [methodId, setMethodId] = useState("");
  const [evidenceIds, setEvidenceIds] = useState<string[]>([]);
  if (!theory) return null;
  const toggleEvidence = (id: string) => setEvidenceIds((current) => current.includes(id)
    ? current.filter((item) => item !== id)
    : current.length < theory.maxEvidence ? [...current, id] : current);
  const canSubmit = culpritId && motiveId && methodId && evidenceIds.length >= theory.minimumEvidence;
  return (
    <div className="theory-node">
      <p className="scene-text">{text(view.node.content?.text)}</p>
      <p className="theory-attempts">Intento {Math.min(theory.attemptsUsed + 1, theory.maxAttempts)} de {theory.maxAttempts}</p>
      {theory.feedback && <div className="theory-feedback" role="alert"><AlertTriangle aria-hidden="true" /><p>{theory.feedback.message}</p></div>}
      <div className="theory-fields">
        <label>Responsable<select value={culpritId} onChange={(event) => setCulpritId(event.target.value)}><option value="">Elige a quién señalar</option>{theory.culprits.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select></label>
        <label>Motivo<select value={motiveId} onChange={(event) => setMotiveId(event.target.value)}><option value="">Elige el motivo</option>{theory.motives.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select></label>
        <label>Método<select value={methodId} onChange={(event) => setMethodId(event.target.value)}><option value="">Elige cómo ocurrió</option>{theory.methods.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}</select></label>
      </div>
      <fieldset className="theory-evidence"><legend>Vincula {theory.minimumEvidence} o {theory.maxEvidence} pruebas</legend>{evidence.map((item) => <label key={item.id} className={evidenceIds.includes(item.id) ? "is-selected" : ""}><input type="checkbox" checked={evidenceIds.includes(item.id)} disabled={!evidenceIds.includes(item.id) && evidenceIds.length >= theory.maxEvidence} onChange={() => toggleEvidence(item.id)} /><span><strong>{item.title}</strong><small>{item.summary}</small></span></label>)}</fieldset>
      <button className="button button-citron button-large scene-primary" type="button" disabled={pending || !canSubmit || !view.node.canAct} onClick={() => action("submit_theory", { culpritId, motiveId, methodId, evidenceIds })}>Presentar teoría <ArrowRight aria-hidden="true" /></button>
    </div>
  );
}

function Lobby({
  view,
  pending,
  action,
  memberToken,
  onReload
}: {
  view: MemberView;
  pending: boolean;
  action: Action;
  memberToken: string;
  onReload: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [switchingMode, setSwitchingMode] = useState(false);
  const joinUrl = `${window.location.origin}/unir?code=${view.room.inviteCode}`;
  const enoughPlayers = view.room.deviceMode === "shared" || view.members.length >= 2;

  const copyInvite = async () => {
    await navigator.clipboard.writeText(joinUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
    api.track("invite_shared", view.room.id, { channel: "clipboard" });
  };

  const share = async () => {
    const shareData = { title: `Únete a ${view.experience.title}`, text: `Código ${view.room.inviteCode}`, url: joinUrl };
    const usedNativeShare = typeof navigator.share === "function";
    if (usedNativeShare) await navigator.share(shareData);
    else await copyInvite();
    api.track("invite_shared", view.room.id, { channel: usedNativeShare ? "native" : "clipboard" });
  };

  const toggleDeviceMode = async () => {
    setSwitchingMode(true);
    try {
      const nextMode = view.room.deviceMode === "multiple" ? "shared" : "multiple";
      await api.switchDeviceMode(view.room.id, memberToken, nextMode);
      onReload();
    } finally {
      setSwitchingMode(false);
    }
  };

  return (
    <div className="lobby-page">
      <header className="lobby-heading">
        <span className="room-code-label">Código de sala</span>
        <strong className="room-code">{view.room.inviteCode}</strong>
        <h1>{view.experience.title}</h1>
        <p>{view.room.deviceMode === "shared" ? "Un teléfono, todas las ventanas." : "Cada persona entra desde su propio teléfono."}</p>
      </header>
      <section className="lobby-windows" aria-label="Personas en la sala">
        {view.members.map((member, index) => (
          <div className={`member-window member-color-${(index % 3) + 1}`} key={member.id}>
            <span className="member-status"><i aria-hidden="true" />{member.status === "connected" ? "Conectado" : "Volviendo"}</span>
            <strong>{member.displayName}</strong>
            <small>{member.isHost ? "Anfitrión" : member.roleIds.join(" · ")}</small>
          </div>
        ))}
        {view.room.deviceMode === "multiple" && view.members.length < 2 && (
          <div className="member-window member-empty">
            <LoaderCircle className="spin" aria-hidden="true" />
            <strong>Esperando otra ventana</strong>
            <small>Comparte el código o el enlace.</small>
          </div>
        )}
      </section>
      <div className="invite-actions">
        <button className="button button-light" type="button" onClick={copyInvite}>
          {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />} {copied ? "Enlace copiado" : "Copiar invitación"}
        </button>
        <button className="button button-light" type="button" onClick={share}><Share2 aria-hidden="true" /> Compartir</button>
        <a
          className="button button-whatsapp"
          href={`https://wa.me/?text=${encodeURIComponent(`Únete a ${view.experience.title}. Código: ${view.room.inviteCode} ${joinUrl}`)}`}
          target="_blank"
          rel="noreferrer"
          onClick={() => api.track("invite_shared", view.room.id, { channel: "whatsapp_link" })}
        >
          <MessageCircleMore aria-hidden="true" /> WhatsApp
        </a>
      </div>
      {view.member.isHost ? (
        <>
          <button
            className="button button-citron button-large lobby-start"
            type="button"
            disabled={pending || switchingMode || !enoughPlayers}
            onClick={() => action("start_experience")}
          >
            {pending ? "Encendiendo…" : <>Empezar la experiencia <ArrowRight aria-hidden="true" /></>}
          </button>
          {view.members.length === 1 && (
            <div style={{ marginTop: "1rem", textAlign: "center" }}>
              <button
                className="button button-light button-small"
                type="button"
                disabled={switchingMode}
                onClick={toggleDeviceMode}
              >
                <Smartphone aria-hidden="true" />{" "}
                {view.room.deviceMode === "multiple"
                  ? "¿Estás en un solo dispositivo? Cambiar a 1 teléfono compartido"
                  : "Cambiar a modo varios teléfonos"}
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="waiting-copy"><Radio aria-hidden="true" /> La persona anfitriona iniciará cuando estén listos.</p>
      )}
      {!enoughPlayers && <p className="start-help">La experiencia necesita al menos dos personas conectadas (o cambiar a modo 1 teléfono).</p>}
    </div>
  );
}

function MessageNode({ view, pending, action }: { view: MemberView; pending: boolean; action: Action }) {
  return (
    <div className="scene-message">
      <p className="scene-text">{text(view.node.content?.text)}</p>
      {text(view.node.content?.transcript) && (
        <details className="transcript"><summary>Leer transcripción</summary><p>{text(view.node.content?.transcript)}</p></details>
      )}
      {text(view.node.content?.aside) && <p className="scene-aside">{text(view.node.content?.aside)}</p>}
      {view.node.canAct && (
        <button className="button button-citron button-large scene-primary" type="button" disabled={pending} onClick={() => action("continue")}>
          Continuar <ArrowRight aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

function ChoiceNode({ view, pending, action }: { view: MemberView; pending: boolean; action: Action }) {
  return (
    <div className="choice-list">
      <p className="scene-text">{text(view.node.content?.text)}</p>
      {view.node.options?.map((option) => (
        <button
          className="choice-option"
          type="button"
          key={option.id}
          disabled={pending || !view.node.canAct}
          onClick={() => action("select_choice", { optionId: option.id })}
        >
          <span><strong>{option.label}</strong>{option.description && <small>{option.description}</small>}</span>
          <ArrowRight aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}

function InputNode({ view, pending, action, onLocalError }: { view: MemberView; pending: boolean; action: Action; onLocalError: (message: string) => void }) {
  const [value, setValue] = useState("");
  const isPuzzle = view.node.type === "puzzle";

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!value.trim()) return;
    const result = await action("submit_input", { value }) as { events?: string[] } | undefined;
    if (result?.events?.includes("input_incorrect")) {
      onLocalError("Esa respuesta todavía no abre la siguiente ventana. Comparen las pistas o pidan una ayuda.");
    } else if (result) {
      setValue("");
      onLocalError("");
    }
  };

  if (view.node.hasSubmitted && !isPuzzle) {
    return (
      <div className="saved-answer" role="status">
        <Check aria-hidden="true" />
        <h2>Tu respuesta quedó guardada.</h2>
        <p>La otra persona no puede verla todavía. La sala avanzará cuando todas las ventanas respondan.</p>
      </div>
    );
  }

  return (
    <form className="scene-input" onSubmit={submit}>
      <p className="scene-text">{text(view.node.content?.text)}</p>
      {text(view.node.content?.privacy_note) && <p className="privacy-note"><EyeOff aria-hidden="true" /> {text(view.node.content?.privacy_note)}</p>}
      <label htmlFor="scene-answer">{text(view.node.content?.input_label) || "Tu respuesta"}</label>
      {isPuzzle ? (
        <input id="scene-answer" value={value} onChange={(event) => setValue(event.target.value)} placeholder={text(view.node.content?.placeholder)} autoComplete="off" />
      ) : (
        <textarea id="scene-answer" value={value} onChange={(event) => setValue(event.target.value)} placeholder={text(view.node.content?.placeholder)} rows={4} />
      )}
      <button className="button button-citron button-large scene-primary" type="submit" disabled={pending || !value.trim() || !view.node.canAct}>
        {pending ? "Guardando…" : isPuzzle ? "Probar respuesta" : "Guardar en privado"} <ArrowRight aria-hidden="true" />
      </button>
      {isPuzzle && (
        <>
          {(() => {
            const status = view.node.hintStatus;
            const hasMore = status ? status.revealedLevel < status.totalHints : true;
            const locked = Boolean(status && !status.nextAvailable && hasMore);
            const cost = status?.nextCost;
            const label = view.node.hint
              ? "Pedir otra pista"
              : "Necesitamos una pista";
            return (
              <>
                {hasMore && (
                  <button className="hint-button" type="button" disabled={pending || locked || !view.node.canAct} onClick={() => action("request_hint")}>
                    <Lightbulb aria-hidden="true" /> {label}
                    {typeof cost === "number" && <span className="hint-cost"> · cuesta {cost} pts</span>}
                  </button>
                )}
                {locked && status?.lockedReason && (
                  <p className="hint-locked" role="status"><LockKeyhole aria-hidden="true" /> {status.lockedReason}</p>
                )}
                {!hasMore && <p className="hint-exhausted" role="status">No quedan más pistas para esta escena.</p>}
              </>
            );
          })()}
        </>
      )}
      {view.node.hint && <div className="hint-reveal"><Lightbulb aria-hidden="true" /><span><strong>Pista {view.node.hint.level}</strong>{view.node.hint.text}</span></div>}
    </form>
  );
}

function RevealNode({ view, pending, action }: { view: MemberView; pending: boolean; action: Action }) {
  return (
    <div className="reveal-node">
      {text(view.node.content?.text) && <p className="scene-text">{text(view.node.content?.text)}</p>}
      {view.node.privateViews?.map((privateView) => (
        <div className="private-reveal" key={privateView.roleId}>
          <div><LockKeyhole aria-hidden="true" /><span>{privateView.roleLabel}</span></div>
          {privateView.label && <strong>{privateView.label}</strong>}
          <p>{privateView.text}</p>
        </div>
      ))}
      {view.node.revealedResponses?.length ? (
        <div className="response-reveal-grid">
          {view.node.revealedResponses.map((response) => (
            <blockquote key={response.memberId}><span>{response.displayName}</span><p>{response.value}</p></blockquote>
          ))}
        </div>
      ) : null}
      {text(view.node.content?.conversation_prompt) && <p className="conversation-prompt"><Eye aria-hidden="true" /> {text(view.node.content?.conversation_prompt)}</p>}
      <button className="button button-citron button-large scene-primary" type="button" disabled={pending || !view.node.canAct} onClick={() => action("acknowledge")}>
        Ya compartimos esta pieza <ArrowRight aria-hidden="true" />
      </button>
    </div>
  );
}

function TimerNode({ view, pending, action }: { view: MemberView; pending: boolean; action: Action }) {
  const [, setTick] = useState(0);
  const timer = view.node.timer;
  const timerRunning = Boolean(timer && !timer.pausedAt);
  const timerEndsAt = timer?.endsAt;
  useEffect(() => {
    if (!timerRunning) return;
    const interval = window.setInterval(() => setTick((value) => value + 1), 250);
    return () => window.clearInterval(interval);
  }, [timerRunning, timerEndsAt]);
  const remaining = timer
    ? timer.pausedAt
      ? Math.ceil((timer.remainingMs ?? 0) / 1000)
      : Math.max(0, Math.ceil((new Date(timer.endsAt).getTime() - Date.now()) / 1000))
    : Number(view.node.content?.seconds ?? 0);
  const minutes = Math.floor(remaining / 60).toString().padStart(2, "0");
  const seconds = (remaining % 60).toString().padStart(2, "0");
  const hasConsequence = Boolean(view.node.timerHasConsequence);
  const timeUp = Boolean(timer) && remaining <= 0;

  return (
    <div className="timer-node">
      <p className="scene-text">{text(view.node.content?.text)}</p>
      {hasConsequence && (
        <p className="timer-consequence" role="note">
          <AlertTriangle aria-hidden="true" />{" "}
          {text(view.node.content?.consequence_note) || "Si el tiempo se agota, la sala avanza con un desenlace más difícil."}
        </p>
      )}
      <div
        className={remaining <= 10 && timer ? "timer-display is-ending" : "timer-display"}
        aria-live="off"
        aria-label={`${minutes} minutos ${seconds} segundos restantes`}
      >
        <span>{minutes}</span><i>:</i><span>{seconds}</span>
      </div>
      {timeUp ? (
        <p className="timer-expired-note" role="status">
          <Clock3 aria-hidden="true" /> El tiempo terminó. La sala avanzará en un momento.
        </p>
      ) : (
        <div className="timer-actions">
          {timer?.pausable && view.member.isHost && (
            <button className="button button-light" type="button" disabled={pending} onClick={() => action(timer.pausedAt ? "timer_resume" : "timer_pause")}>
              {timer.pausedAt ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />} {timer.pausedAt ? "Reanudar" : "Pausar"}
            </button>
          )}
          <button
            className="button button-citron"
            type="button"
            disabled={pending || (!view.member.isHost && remaining > 0)}
            onClick={() => action("timer_complete", { manual: view.member.isHost })}
          >
            {view.member.isHost ? "Terminar ronda ahora" : "Terminar ronda"} <ArrowRight aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
}

function WaitNode({ view, pending, action }: { view: MemberView; pending: boolean; action: Action }) {
  return (
    <div className="ready-node" role="status">
      {view.node.isReady ? <Check aria-hidden="true" /> : <UsersRound aria-hidden="true" />}
      <h2>{view.node.isReady ? "Tu ventana está lista" : view.node.label}</h2>
      <p>{text(view.node.content?.text)}</p>
      {view.node.isReady ? (
        <span className="ready-confirmation"><LoaderCircle className="spin" aria-hidden="true" /> Esperando al resto de la sala…</span>
      ) : (
        <button className="button button-citron button-large scene-primary" type="button" disabled={pending || !view.node.canAct} onClick={() => action("set_ready")}>
          {pending ? "Guardando…" : "Ya estoy listo"} <ArrowRight aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

const positiveRatingTags = (Object.entries(RATING_TAGS) as Array<[RatingTagId, (typeof RATING_TAGS)[RatingTagId]]>).filter(([, tag]) => tag.public);
const improvementRatingTags = (Object.entries(RATING_TAGS) as Array<[RatingTagId, (typeof RATING_TAGS)[RatingTagId]]>).filter(([, tag]) => !tag.public);

function EndNode({ view, memberToken }: { view: MemberView; memberToken: string }) {
  const [rating, setRating] = useState(0);
  const [tags, setTags] = useState<RatingTagId[]>([]);
  const [ratingState, setRatingState] = useState<"loading" | "idle" | "saving" | "saved" | "error">("loading");
  const shareText = text(view.node.content?.share_text);

  useEffect(() => {
    api.rating(view.room.id, memberToken)
      .then((saved) => {
        if (saved) { setRating(saved.value); setTags(saved.tags); }
        setRatingState("idle");
      })
      .catch(() => setRatingState("error"));
  }, [memberToken, view.room.id]);

  const selectRating = (value: number) => {
    setRating(value);
    setTags([]);
    setRatingState("idle");
  };
  const toggleTag = (tag: RatingTagId) => {
    setTags((current) => current.includes(tag) ? current.filter((item) => item !== tag) : current.length < 3 ? [...current, tag] : current);
    setRatingState("idle");
  };
  const saveRating = async () => {
    setRatingState("saving");
    try {
      await api.saveRating(view.room.id, memberToken, rating, tags);
      setRatingState("saved");
    } catch {
      setRatingState("error");
    }
  };
  const share = async () => {
    if (navigator.share) await navigator.share({ title: view.experience.title, text: shareText });
    else await navigator.clipboard.writeText(shareText);
  };

  return (
    <div className="end-node">
      <div className="end-burst" aria-hidden="true"><span /><span /><span /><span /></div>
      <p className="scene-text">{text(view.node.content?.text)}</p>
      {typeof view.publicState.score === "number" && <div className="final-score"><span>Puntuación</span><strong>{view.publicState.score}</strong></div>}
      {view.publicState.scores && (
        <div className="team-scores">
          {Object.entries(view.publicState.scores).map(([team, score]) => <div key={team}><span>Equipo {team}</span><strong>{score}</strong></div>)}
        </div>
      )}
      <section className="rating-panel">
        <h2>¿Qué tan buena fue esta noche?</h2>
        <p>Tu valoración es privada y puedes cambiarla después.</p>
        {ratingState === "loading" ? <span className="rating-loading"><LoaderCircle className="spin" aria-hidden="true" /> Recuperando tu valoración…</span> : (
          <>
            <fieldset className="star-row"><legend>Califica de una a cinco estrellas</legend>
              {[1, 2, 3, 4, 5].map((value) => (
                <label key={value} aria-label={`${value} ${value === 1 ? "estrella" : "estrellas"}`}>
                  <input type="radio" name="experience-rating" value={value} checked={rating === value} onChange={() => selectRating(value)} />
                  <Star fill={rating >= value ? "currentColor" : "none"} aria-hidden="true" />
                </label>
              ))}
            </fieldset>
            {rating > 0 && (
              <fieldset className="rating-tags"><legend>{rating >= 4 ? "¿Qué hizo que funcionara?" : "¿Qué podríamos mejorar?"}<small>Elige hasta tres.</small></legend>
                {(rating >= 4 ? positiveRatingTags : improvementRatingTags).map(([id, tag]) => (
                  <label key={id} className={tags.includes(id) ? "is-selected" : ""}>
                    <input type="checkbox" checked={tags.includes(id)} disabled={!tags.includes(id) && tags.length >= 3} onChange={() => toggleTag(id)} />
                    {tag.label}
                  </label>
                ))}
              </fieldset>
            )}
            {ratingState === "error" && <p className="rating-error" role="alert">No pudimos guardar todavía. Tu selección sigue aquí para que reintentes.</p>}
            {ratingState === "saved" && <p className="rating-saved" role="status"><Check aria-hidden="true" /> Valoración guardada. Gracias por abrirnos esta ventana.</p>}
            <button className="button button-citron" type="button" disabled={!rating || ratingState === "saving"} onClick={saveRating}>
              {ratingState === "saving" ? "Guardando…" : ratingState === "saved" ? "Actualizar valoración" : "Guardar valoración"}
            </button>
          </>
        )}
      </section>
      <div className="end-actions">
        <button className="button button-light" type="button" onClick={share}><Share2 aria-hidden="true" /> Compartir sin spoilers</button>
        <Link className="button button-citron" to="/catalogo">Elegir la siguiente <ArrowRight aria-hidden="true" /></Link>
      </div>
    </div>
  );
}

function SceneRenderer({ view, pending, action, onLocalError, memberToken }: { view: MemberView; pending: boolean; action: Action; onLocalError: (message: string) => void; memberToken: string }) {
  if (view.node.type === "message") return <MessageNode view={view} pending={pending} action={action} />;
  if (view.node.type === "choice") return <ChoiceNode view={view} pending={pending} action={action} />;
  if (view.node.type === "input" || view.node.type === "puzzle") return <InputNode view={view} pending={pending} action={action} onLocalError={onLocalError} />;
  if (view.node.type === "reveal") return <RevealNode view={view} pending={pending} action={action} />;
  if (view.node.type === "timer") return <TimerNode view={view} pending={pending} action={action} />;
  if (view.node.type === "theory") return <TheoryNode view={view} pending={pending} action={action} />;
  if (view.node.type === "end") return <EndNode view={view} memberToken={memberToken} />;
  if (view.node.type === "wait") return <WaitNode view={view} pending={pending} action={action} />;
  return <InlineNotice tone="error">Este tipo de escena todavía no tiene una vista disponible.</InlineNotice>;
}

export function RoomPage() {
  const { roomId = "" } = useParams();
  const { access, view, error, setError, connection, pending, reload, send } = useRoom(roomId);
  const [caseFileOpen, setCaseFileOpen] = useState(false);

  if (!access) {
    return (
      <ErrorState message="Este navegador no conserva el acceso de miembro. Vuelve a entrar con el código de la sala." />
    );
  }
  if (!view && !error) return <LoadingState label="Recuperando la última escena…" />;
  if (!view) return <ErrorState message={error} onRetry={reload} />;
  if (view.room.status === "lobby") return <Lobby view={view} pending={pending} action={send} memberToken={access.memberToken} onReload={reload} />;

  return (
    <div className={`player-shell player-${view.experience.format}`}>
      <header className="player-topbar">
        <div>
          <span>{view.experience.title}</span>
          <strong>{view.publicState.progressLabel}</strong>
        </div>
        <ConnectionBadge state={connection} />
        {view.publicState.caseFile && <button className="icon-button" type="button" aria-label="Abrir expediente" aria-expanded={caseFileOpen} onClick={() => setCaseFileOpen(true)}><ClipboardList aria-hidden="true" /></button>}
        {view.member.isHost && view.room.status !== "ended" && (
          <button className="icon-button" type="button" aria-label={view.room.status === "paused" ? "Reanudar experiencia" : "Pausar experiencia"} onClick={() => send(view.room.status === "paused" ? "resume_experience" : "pause_experience")}>
            {view.room.status === "paused" ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />}
          </button>
        )}
      </header>
      <aside className="player-rail" aria-label="Estado de la sala">
        <div className="floor-progress">
          <span className="floor-dot is-past" /><span className="floor-line is-lit" /><span className="floor-dot is-current" /><span className="floor-line" /><span className="floor-dot" />
        </div>
        <div className="live-members">
          <strong><UsersRound aria-hidden="true" /> Ventanas</strong>
          {view.members.map((member) => (
            <span key={member.id} className={member.status === "connected" ? "member-live" : "member-away"}>
              <i aria-hidden="true" /> {member.displayName}{member.id === view.member.id ? " · tú" : ""}
            </span>
          ))}
        </div>
        {typeof view.publicState.score === "number" && <div className="rail-score"><Sparkles aria-hidden="true" /><span>Puntos</span><strong>{view.publicState.score}</strong></div>}
      </aside>
      <main className="scene-stage">
        <div className="scene-status"><span>{view.node.eyebrow ?? `Escena ${view.room.revision}`}</span><span>Revisión {view.room.revision}</span></div>
        <article className="scene-window">
          <h1>{view.node.label}</h1>
          {error && <InlineNotice tone="error">{error}</InlineNotice>}
          <SceneMedia view={view} />
          <SceneRenderer view={view} pending={pending} action={send} onLocalError={setError} memberToken={access.memberToken} />
        </article>
        <p className="scene-save-status"><Check aria-hidden="true" /> Progreso guardado en la sala</p>
      </main>
      <CaseFile view={view} pending={pending} action={send} open={caseFileOpen} onClose={() => setCaseFileOpen(false)} />
      {view.room.status === "paused" && (
        <div className="pause-screen" role="dialog" aria-modal="true" aria-label="Experiencia pausada">
          <Clock3 aria-hidden="true" />
          <h2>La noche está en pausa.</h2>
          <p>El progreso está guardado. Sólo la persona anfitriona puede reanudar.</p>
          {view.member.isHost && <button className="button button-citron" type="button" onClick={() => send("resume_experience")}><Play aria-hidden="true" /> Reanudar</button>}
        </div>
      )}
      {connection !== "connected" && connection !== "polling" && (
        <div className="reconnect-bar" role="status">
          <RotateCcw className="spin" aria-hidden="true" /> {connection === "offline" ? "Sin conexión. Conservamos esta escena." : "Reconectando la sala…"}
        </div>
      )}
    </div>
  );
}
